import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Fade,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { Add, PictureAsPdf } from "@mui/icons-material";
import type {
  Club,
  ClubMember,
  InvoiceByPeriodItem,
  OneOffSession,
  TrainingGroup,
  UpcomingTrainingDate,
} from "./types";
import { useClubs } from "./useClubs";
import { useHandlers } from "./useHandlers";
import ClubCard from "./ClubCard";
import AddClubMemberDialog from "./AddClubMemberDialog";
import CreateGroupDialog from "./CreateGroupDialog";
import AddGroupMemberDialog from "./AddGroupMemberDialog";
import RemoveMemberDialog from "./RemoveMemberDialog";
import ArchiveGroupDialog from "./ArchiveGroupDialog";
import RenameGroupDialog from "./RenameGroupDialog";
import GroupDefaultFeeDialog from "./GroupDefaultFeeDialog";
import RemoveGroupMemberDialog from "./RemoveGroupMemberDialog";
import NotesDialog from "./NotesDialog";
import IssueInvoiceDialog, {
  IssueInvoiceTarget,
} from "./IssueInvoiceDialog";
import VoidInvoiceDialog from "./VoidInvoiceDialog";
import CreateClubDialog from "./CreateClubDialog";
import VenueHireSettingsDialog from "./VenueHireSettingsDialog";
import AddAvailabilityDialog from "./AddAvailabilityDialog";
import BlockTimeDialog from "./BlockTimeDialog";
import CreateOneOffSessionDialog from "./CreateOneOffSessionDialog";
import AddOneOffParticipantDialog from "./AddOneOffParticipantDialog";
import IssueOneOffInvoicesDialog, {
  IssueOneOffInvoicesTarget,
} from "./IssueOneOffInvoicesDialog";
import CancelOneOffSessionDialog from "./CancelOneOffSessionDialog";
import SessionBookingSettingsDialog from "./SessionBookingSettingsDialog";
import { featureFlags } from "../../utils/featureFlags";

const CLUB_MANAGEMENT_GUIDE_URL =
  "https://s3.eu-west-2.amazonaws.com/easyagility.co.uk/club-management-admin-guide.pdf";

export type {
  ClubMember,
  UpcomingTrainingDate,
  TrainingGroup,
  InvoiceByPeriodItem,
  InvoicesByPeriod,
  Club,
  Handler,
} from "./types";

const AgilityClubs = (): JSX.Element => {
  const theme = useTheme();
  const { clubs, loading, error, setError, refetch } = useClubs();

  const handleHandlersError = useCallback(
    (message: string) => setError(message),
    [setError],
  );
  const { handlers, loading: handlersLoading, ensureLoaded: ensureHandlers } =
    useHandlers(handleHandlersError);

  const [addClubMemberClub, setAddClubMemberClub] = useState<Club | null>(null);
  const [createGroupClubId, setCreateGroupClubId] = useState<number | null>(
    null,
  );
  const [createOneOffSessionClubId, setCreateOneOffSessionClubId] = useState<
    number | null
  >(null);
  const [addOneOffParticipantTarget, setAddOneOffParticipantTarget] = useState<{
    club: Club;
    session: OneOffSession;
  } | null>(null);
  const [issueOneOffInvoicesTarget, setIssueOneOffInvoicesTarget] =
    useState<IssueOneOffInvoicesTarget | null>(null);
  const [cancelOneOffSessionTarget, setCancelOneOffSessionTarget] = useState<{
    session: OneOffSession;
  } | null>(null);
  const [sessionBookingSettingsTarget, setSessionBookingSettingsTarget] =
    useState<OneOffSession | null>(null);
  const [addGroupMemberTarget, setAddGroupMemberTarget] = useState<{
    club: Club;
    group: TrainingGroup;
  } | null>(null);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<{
    clubId: number;
    member: ClubMember;
  } | null>(null);
  const [archiveGroupTarget, setArchiveGroupTarget] = useState<{
    group: TrainingGroup;
  } | null>(null);
  const [renameGroupTarget, setRenameGroupTarget] = useState<{
    clubId: number;
    group: TrainingGroup;
  } | null>(null);
  const [groupDefaultFeeTarget, setGroupDefaultFeeTarget] = useState<{
    clubId: number;
    group: TrainingGroup;
  } | null>(null);
  const [removeGroupMemberTarget, setRemoveGroupMemberTarget] = useState<{
    group: TrainingGroup;
    member: ClubMember;
  } | null>(null);
  const [notesDialogTarget, setNotesDialogTarget] = useState<{
    group: TrainingGroup;
    date: UpcomingTrainingDate;
  } | null>(null);
  const [invoiceTarget, setInvoiceTarget] = useState<IssueInvoiceTarget | null>(
    null,
  );
  const [voidInvoiceTarget, setVoidInvoiceTarget] =
    useState<InvoiceByPeriodItem | null>(null);
  const [createClubOpen, setCreateClubOpen] = useState(false);
  const [venueHireSettingsClub, setVenueHireSettingsClub] = useState<Club | null>(
    null,
  );
  const [addAvailabilityClub, setAddAvailabilityClub] = useState<Club | null>(
    null,
  );
  const [blockTimeClub, setBlockTimeClub] = useState<Club | null>(null);
  const [expandedVenueHireClubId, setExpandedVenueHireClubId] = useState<
    number | null
  >(null);
  const [venueHireRefreshKey, setVenueHireRefreshKey] = useState(0);

  const handleVenueHireDataChange = useCallback(async () => {
    await refetch({ silent: true });
    setVenueHireRefreshKey((key) => key + 1);
  }, [refetch]);

  const [expandedTrainingGroupKey, setExpandedTrainingGroupKey] = useState<
    string | null
  >(null);
  const [expandedOneOffSessionKey, setExpandedOneOffSessionKey] = useState<
    string | null
  >(null);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const shouldScrollToGroupRef = useRef(false);
  const scrollToExpandedGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      shouldScrollToGroupRef.current &&
      expandedTrainingGroupKey &&
      scrollToExpandedGroupRef.current
    ) {
      scrollToExpandedGroupRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      shouldScrollToGroupRef.current = false;
    }
  }, [expandedTrainingGroupKey, clubs]);

  useEffect(() => {
    if (clubs.length === 0) {
      setSelectedClubId(null);
      return;
    }

    if (
      selectedClubId == null ||
      !clubs.some((club) => club.id === selectedClubId)
    ) {
      setSelectedClubId(clubs[0].id);
    }
  }, [clubs, selectedClubId]);

  const showClubTabs = clubs.length >= 2;
  const selectedClub = useMemo(
    () => clubs.find((club) => club.id === selectedClubId) ?? clubs[0] ?? null,
    [clubs, selectedClubId],
  );
  const selectedClubTabIndex = useMemo(() => {
    const index = clubs.findIndex((club) => club.id === selectedClub?.id);
    return index === -1 ? 0 : index;
  }, [clubs, selectedClub?.id]);

  const showError = useCallback(
    (message: string) => setError(message),
    [setError],
  );

  const handleAddGroupMemberSuccess = useCallback(
    async (groupKey: string) => {
      shouldScrollToGroupRef.current = true;
      setExpandedTrainingGroupKey(groupKey);
      await refetch();
    },
    [refetch],
  );

  const handleRenameGroupSuccess = useCallback(
    async (groupKey: string) => {
      shouldScrollToGroupRef.current = true;
      setExpandedTrainingGroupKey(groupKey);
      await refetch({ silent: true });
    },
    [refetch],
  );

  const handleIssueInvoiceSuccess = useCallback(async () => {
    await refetch({ silent: true });
  }, [refetch]);

  const handleOneOffSessionSuccess = useCallback(
    async (sessionKey?: string) => {
      if (sessionKey) {
        setExpandedOneOffSessionKey(sessionKey);
      }
      await refetch({ silent: true });
    },
    [refetch],
  );

  const renderClubCard = (club: Club) => (
    <ClubCard
      key={club.id}
      club={club}
      expandedTrainingGroupKey={expandedTrainingGroupKey}
      setExpandedTrainingGroupKey={setExpandedTrainingGroupKey}
      scrollToExpandedGroupRef={scrollToExpandedGroupRef}
      onAddClubMember={(clubId) => {
        const found = clubs.find((c) => c.id === clubId);
        if (found) setAddClubMemberClub(found);
      }}
      onRemoveClubMember={(clubId, member) =>
        setRemoveMemberTarget({ clubId, member })
      }
      onCreateGroup={(clubId) => setCreateGroupClubId(clubId)}
      onCreateOneOffSession={(clubId) => setCreateOneOffSessionClubId(clubId)}
      onAddGroupMember={(c, g) => setAddGroupMemberTarget({ club: c, group: g })}
      onAddOneOffParticipant={(c, s) =>
        setAddOneOffParticipantTarget({ club: c, session: s })
      }
      onIssueInvoice={(c, g) =>
        setInvoiceTarget({
          clubId: c.id,
          groupName: g.group_name,
          groupMembers: g.members,
          clubMembers: c.members,
          defaultFee: g.default_fee ?? 0,
          defaultFeeBasis: g.default_fee_basis ?? "invoice",
        })
      }
      onIssueOneOffInvoices={(s) => setIssueOneOffInvoicesTarget({ session: s })}
      onCancelOneOffSession={(s) => setCancelOneOffSessionTarget({ session: s })}
      onSessionBookingSettings={(s) => setSessionBookingSettingsTarget(s)}
      onOneOffSessionRefresh={() => refetch({ silent: true })}
      onOneOffSessionError={showError}
      expandedOneOffSessionKey={expandedOneOffSessionKey}
      setExpandedOneOffSessionKey={setExpandedOneOffSessionKey}
      onArchiveGroup={(g) => setArchiveGroupTarget({ group: g })}
      onRenameGroup={(c, g) => setRenameGroupTarget({ clubId: c.id, group: g })}
      onSetGroupDefaultFee={(c, g) =>
        setGroupDefaultFeeTarget({ clubId: c.id, group: g })
      }
      onRemoveGroupMember={(g, member) =>
        setRemoveGroupMemberTarget({ group: g, member })
      }
      onEditNotes={(g, d) => setNotesDialogTarget({ group: g, date: d })}
      expandedVenueHireClubId={expandedVenueHireClubId}
      onVenueHireExpandChange={setExpandedVenueHireClubId}
      onVenueHireSettings={(c) => setVenueHireSettingsClub(c)}
      onAddVenueAvailability={(c) => setAddAvailabilityClub(c)}
      onBlockVenueTime={(c) => setBlockTimeClub(c)}
      onVenueHireRefresh={handleVenueHireDataChange}
      onVenueHireError={showError}
      venueHireRefreshKey={venueHireRefreshKey}
      onVoidInvoice={(invoice) => setVoidInvoiceTarget(invoice)}
    />
  );

  const pageSx = { py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } };

  if (loading) {
    return (
      <Container maxWidth={false} sx={pageSx}>
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={pageSx}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={pageSx}>
      <Fade in timeout={600}>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mb: { xs: 2, sm: 4 },
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: { xs: "1.5rem", sm: "2.125rem" },
              }}
            >
              Club Administration
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdf />}
              component="a"
              href={CLUB_MANAGEMENT_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flexShrink: 0 }}
            >
              Club Management guide
            </Button>
          </Box>

          {clubs.length === 0 ? (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setCreateClubOpen(true)}
                >
                  Create your club
                </Button>
              }
            >
              No clubs found. Set up a sandbox club to try club administration.
            </Alert>
          ) : (
            <Box>
              {showClubTabs && (
                <Tabs
                  value={selectedClubTabIndex}
                  onChange={(_, index: number) =>
                    setSelectedClubId(clubs[index]?.id ?? null)
                  }
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                  aria-label="Club administration tabs"
                >
                  {clubs.map((club) => (
                    <Tab
                      key={club.id}
                      label={
                        club.is_sandbox ? `${club.name} (Sandbox)` : club.name
                      }
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    />
                  ))}
                </Tabs>
              )}

              {selectedClub && renderClubCard(selectedClub)}
            </Box>
          )}
        </Box>
      </Fade>

      <AddClubMemberDialog
        open={addClubMemberClub != null}
        club={addClubMemberClub}
        handlers={handlers}
        handlersLoading={handlersLoading}
        onEnsureHandlersLoaded={ensureHandlers}
        onClose={() => setAddClubMemberClub(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      <CreateGroupDialog
        open={createGroupClubId != null}
        clubId={createGroupClubId}
        onClose={() => setCreateGroupClubId(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      <AddGroupMemberDialog
        target={addGroupMemberTarget}
        onClose={() => setAddGroupMemberTarget(null)}
        onSuccess={handleAddGroupMemberSuccess}
        onError={showError}
      />

      <RemoveMemberDialog
        target={removeMemberTarget}
        onClose={() => setRemoveMemberTarget(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      <ArchiveGroupDialog
        target={archiveGroupTarget}
        onClose={() => setArchiveGroupTarget(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      <RenameGroupDialog
        target={renameGroupTarget}
        onClose={() => setRenameGroupTarget(null)}
        onSuccess={handleRenameGroupSuccess}
        onError={showError}
      />

      <GroupDefaultFeeDialog
        target={groupDefaultFeeTarget}
        onClose={() => setGroupDefaultFeeTarget(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      <RemoveGroupMemberDialog
        target={removeGroupMemberTarget}
        onClose={() => setRemoveGroupMemberTarget(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      <NotesDialog
        target={notesDialogTarget}
        onClose={() => setNotesDialogTarget(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      <IssueInvoiceDialog
        target={invoiceTarget}
        onClose={() => setInvoiceTarget(null)}
        onSuccess={handleIssueInvoiceSuccess}
        onError={showError}
      />

      <VoidInvoiceDialog
        target={voidInvoiceTarget}
        onClose={() => setVoidInvoiceTarget(null)}
        onSuccess={() => refetch({ silent: true })}
        onError={showError}
      />

      {featureFlags.oneOffSessions && (
        <>
          <CreateOneOffSessionDialog
            open={createOneOffSessionClubId != null}
            clubId={createOneOffSessionClubId}
            onClose={() => setCreateOneOffSessionClubId(null)}
            onSuccess={() => refetch({ silent: true })}
            onError={showError}
          />
          <AddOneOffParticipantDialog
            target={addOneOffParticipantTarget}
            handlers={handlers}
            handlersLoading={handlersLoading}
            onEnsureHandlersLoaded={ensureHandlers}
            onClose={() => setAddOneOffParticipantTarget(null)}
            onSuccess={() =>
              addOneOffParticipantTarget
                ? handleOneOffSessionSuccess(
                    `${addOneOffParticipantTarget.club.id}-one-off-${addOneOffParticipantTarget.session.id}`,
                  )
                : refetch({ silent: true })
            }
            onError={showError}
          />
          <IssueOneOffInvoicesDialog
            target={issueOneOffInvoicesTarget}
            onClose={() => setIssueOneOffInvoicesTarget(null)}
            onSuccess={handleIssueInvoiceSuccess}
            onError={showError}
          />
          <CancelOneOffSessionDialog
            target={cancelOneOffSessionTarget}
            onClose={() => setCancelOneOffSessionTarget(null)}
            onSuccess={() => refetch({ silent: true })}
            onError={showError}
          />
          <SessionBookingSettingsDialog
            session={sessionBookingSettingsTarget}
            onClose={() => setSessionBookingSettingsTarget(null)}
            onSuccess={() => refetch({ silent: true })}
            onError={showError}
          />
        </>
      )}

      <CreateClubDialog
        open={createClubOpen}
        onClose={() => setCreateClubOpen(false)}
        onSuccess={() => refetch()}
        onError={showError}
      />

      {featureFlags.venueHire && (
        <>
          <VenueHireSettingsDialog
            club={venueHireSettingsClub}
            onClose={() => setVenueHireSettingsClub(null)}
            onSuccess={handleVenueHireDataChange}
            onError={showError}
          />
          <AddAvailabilityDialog
            club={addAvailabilityClub}
            onClose={() => setAddAvailabilityClub(null)}
            onSuccess={handleVenueHireDataChange}
            onError={showError}
          />
          <BlockTimeDialog
            club={blockTimeClub}
            onClose={() => setBlockTimeClub(null)}
            onSuccess={handleVenueHireDataChange}
            onError={showError}
          />
        </>
      )}
    </Container>
  );
};

export default AgilityClubs;
